import './LoginFlow.css';
import Modal from 'react-modal';
import { CloseButton } from './CloseButton.tsx';
import { useLoginSession } from './SessionContext.tsx';
import { useEffect, useRef, useState } from 'react';
import { useKeyValueStore, useRawLocalState, useSupabaseClient } from './GlobalState.tsx';
import { MODAL_CONTENT_DEFAULTS, MODAL_OVERLAY_DEFAULTS, UserCancellationError } from './utils.ts';
import Spinner from './Spinner.tsx';
import { useInGameNotificationContext } from './InGameNotificationContext.tsx';
import { usePuzzleIndex } from './PuzzleIndex.tsx';
import { track } from './umami.ts';
import * as Sentry from '@sentry/capacitor';

export function LoginFlow() {
    const sessionContext = useLoginSession();
    const store = useKeyValueStore();
    const initialLocalState = useRawLocalState();
    const supabaseClient = useSupabaseClient();
    const inGameNotificationsContext = useInGameNotificationContext();
    const { puzzleIndex } = usePuzzleIndex();

    const emailInputRef = useRef<HTMLInputElement>(null);

    const otpRef = useRef<HTMLInputElement>(null);

    const [signInModalIsOpen, setSignInModalIsOpen] = useState<boolean>(false);
    const [signInVerificationModalIsOpen, setSignInVerificationModalIsOpen] = useState<boolean>(false);
    const [enteredEmail, setEnteredEmail] = useState<string>('');
    const [signInCoolDownTimeRemaining, setSignInCoolDownTimeRemaining] = useState<number>(
        getInitialSignInCoolDownTimeRemaining()
    );
    const [loading, setLoading] = useState<boolean>(false);

    function getInitialSignInCoolDownTimeRemaining() {
        return parseInt(initialLocalState.signInCoolDownTimeRemaining ?? '0', 10);
    }

    function openSignInModal() {
        setSignInModalIsOpen(true);
    }

    function forceCloseSignInModal() {
        if (loading) {
            return;
        }
        sessionContext.reject(new UserCancellationError('User cancelled sign in'));
        closeSignInModal();
    }

    function closeSignInModal() {
        setSignInModalIsOpen(false);
    }

    function openSignInVerificationModal() {
        setSignInVerificationModalIsOpen(true);
    }

    function forceCloseSignInVerificationModal() {
        sessionContext.reject(new UserCancellationError('User cancelled verification'));
        closeSignInVerificationModal();
    }

    function closeSignInVerificationModal() {
        setSignInVerificationModalIsOpen(false);
    }

    useEffect(() => {
        void store.setValue('signInCoolDownTimeRemaining', signInCoolDownTimeRemaining.toString());
    }, [signInCoolDownTimeRemaining]);

    useEffect(() => {
        const intervalId = setInterval(() => {
            setSignInCoolDownTimeRemaining((time) => Math.max(0, time - 1));
            if (signInCoolDownTimeRemaining == 0) {
                clearInterval(intervalId);
            }
        }, 1000);
        return () => clearInterval(intervalId);
    }, [signInCoolDownTimeRemaining]);

    useEffect(() => {
        if (sessionContext.isOpen) {
            openSignInModal();
        }
    }, [sessionContext.isOpen]);

    return (
        <div className='login-flow-container'>
            <Modal
                contentLabel='Login modal'
                overlayClassName='login-modal'
                isOpen={signInModalIsOpen}
                style={{
                    content: {
                        ...MODAL_CONTENT_DEFAULTS,
                        maxWidth: '250px',
                        margin: 'auto auto',
                        height: 'fit-content',
                        maxHeight: '90%',
                        overflowY: 'auto',
                        padding: '30px',
                    },
                    overlay: MODAL_OVERLAY_DEFAULTS,
                }}>
                <CloseButton onClose={forceCloseSignInModal} />
                <div className='sign-in-modal'>
                    <h2 className='center-text'>Sign In to Triplit</h2>
                    <p>Enter your email to sync your stats and subscriptions across all your devices.</p>
                    <input
                        aria-label='Email'
                        name='email-input'
                        className='email-input'
                        ref={emailInputRef}
                        type='email'
                        placeholder='you@example.com'
                        autoComplete='email'
                        value={enteredEmail}
                        autoCapitalize='off'
                        onChange={(e) => setEnteredEmail(e.target.value)}></input>
                    {signInCoolDownTimeRemaining > 0 && (
                        <p className='cooldown-timer-text'>
                            Please wait {signInCoolDownTimeRemaining} second
                            {signInCoolDownTimeRemaining == 1 ? '' : 's'} before trying again.
                        </p>
                    )}
                    {loading ? (
                        <Spinner className='mini-spinner' />
                    ) : (
                        <button
                            aria-label='Sign In'
                            name='sign-in-button'
                            className='game-button play-button cursor-pointer sign-in-button'
                            disabled={signInCoolDownTimeRemaining > 0}
                            onClick={async () => {
                                setLoading(true);
                                try {
                                    if (!emailInputRef.current?.checkValidity()) {
                                        inGameNotificationsContext.displayNotification(
                                            'Please enter a valid email address.',
                                            3000
                                        );
                                        return;
                                    }
                                    await supabaseClient.auth.signInWithOtp({
                                        email: enteredEmail,
                                    });
                                    setLoading(false);
                                    setSignInCoolDownTimeRemaining(60);
                                    closeSignInModal();
                                    openSignInVerificationModal();
                                } finally {
                                    setLoading(false);
                                }
                            }}>
                            Continue
                        </button>
                    )}
                </div>
            </Modal>
            <Modal
                overlayClassName='login-verification-modal'
                isOpen={signInVerificationModalIsOpen}
                style={{
                    content: {
                        ...MODAL_CONTENT_DEFAULTS,
                        maxWidth: '250px',
                        margin: 'auto auto',
                        height: 'fit-content',
                        maxHeight: '90%',
                        overflowY: 'auto',
                        padding: '30px',
                    },
                    overlay: MODAL_OVERLAY_DEFAULTS,
                }}>
                <CloseButton onClose={forceCloseSignInVerificationModal} />
                <div className='verification-modal'>
                    <h2> Verification </h2>
                    <p>Please enter the code that was sent to your email account.</p>
                    <input
                        aria-label='Verification input'
                        name='verification-input'
                        className='verification-input'
                        type='text'
                        ref={otpRef}
                        defaultValue=''></input>
                    <div className='row'>
                        <button
                            aria-label='Back button'
                            name='sign-in-back-button'
                            className='button-secondary verify-button game-button play-button cursor-pointer'
                            onClick={() => {
                                console.info('Received email address, going to OTP verification');
                                closeSignInVerificationModal();
                                openSignInModal();
                            }}>
                            Back
                        </button>
                        {loading ? (
                            <Spinner className='mini-spinner' />
                        ) : (
                            <button
                                aria-label='Verification button'
                                className='verify-button game-button play-button cursor-pointer'
                                onClick={async () => {
                                    try {
                                        const res = await supabaseClient.auth.verifyOtp({
                                            token: otpRef.current!.value,
                                            email: enteredEmail,
                                            type: 'email',
                                        });
                                        if (res.error) {
                                            throw res.error;
                                        }
                                        if (!res.data.session) {
                                            throw new Error('No session returned after OTP verification');
                                        }
                                        // Transfer any anonymous data to the new user ID
                                        setLoading(true);
                                        await puzzleIndex.mergeAnonymousDataWithUserIfNeeded(res.data.session.user.id);
                                        // void doSync();
                                        setLoading(false);
                                        // Update the session *after* transferring the data, to ensure that the Game component picks up
                                        // the merged data upon re-render.
                                        sessionContext.resolve(res.data.session);
                                        closeSignInVerificationModal();
                                        inGameNotificationsContext.displayNotification('Successfully signed in!', 3000);
                                        track('user_signed_in');
                                        console.info('User signed in successfully');
                                    } catch (error) {
                                        console.error('Error during OTP verification:', error);
                                        Sentry.captureException(error);
                                        inGameNotificationsContext.displayNotification(
                                            'The code you entered is invalid. Please try re-entering the code, or go back to resend the code.',
                                            3000
                                        );
                                    }
                                }}>
                                Submit
                            </button>
                        )}
                    </div>
                </div>
            </Modal>
        </div>
    );
}
